/**
 * Invoice lifecycle.
 *
 * The one rule that shapes everything: a number is drawn at issue, never at
 * draft. § 14 Abs. 4 Nr. 4 UStG wants a sequential number assigned once, and a
 * tax auditor reads a gap as a suppressed invoice — so a draft the operator
 * abandons must not have consumed one.
 *
 * The second rule: an issued invoice is not edited. A wrong invoice is
 * cancelled by a Stornorechnung and reissued. That is both the commercial
 * convention and what GoBD Rz. 107-112 requires of a booked document.
 */

import type { AuditContext } from '@/db/audit';
import { addDays, nowIso, todayIso } from '@/domain/dates';
import { lineNetCents, netFromGross, summariseVat } from '@/domain/money';
import {
  DEFAULT_NUMBER_CIRCLE,
  nextInvoiceNumber,
  numberCircleFor,
  type NumberCircleConfig,
} from '@/domain/invoiceNumber';
import type {
  Address,
  CompanySettings,
  DunningLevel,
  Invoice,
  InvoiceLine,
  Passenger,
  Trip,
  Uuid,
} from '@/domain/types';
import { exemptionNote } from '@/domain/vat';
import { de } from '@/i18n/de';
import type { Dictionary } from '@/i18n/de';
import { allInvoiceNumbers, invoiceRepo, tripRepo } from '@/db/repositories';
import { formatDate } from '@/domain/dates';
import { formatKm } from '@/domain/money';
import { passengerDisplayName } from '@/suggest';

/** § 33 UStDV — the short form applies at or below this gross amount. */
export const KLEINBETRAG_LIMIT_CENTS = 25_000;

export interface DraftInvoiceParams {
  company: CompanySettings;
  trips: readonly Trip[];
  passengers: ReadonlyMap<Uuid, Passenger>;
  addresses: ReadonlyMap<Uuid, Address>;
  customerPassengerId?: Uuid;
  customerName?: string;
  customerAddress?: string;
  customerVatId?: string;
  issueDate?: string;
  paymentTermDays?: number;
}

/**
 * Build an unsaved draft from a set of trips.
 *
 * Each trip becomes one line with the passenger, the route and the date, which
 * is what § 14 Abs. 4 Nr. 5 and 6 UStG require: the nature of the service and
 * when it was performed, in terms a reader recognises.
 */
export function buildDraftInvoice(params: DraftInvoiceParams): Invoice {
  const issueDate = params.issueDate ?? todayIso();
  const trips = [...params.trips].sort((a, b) => a.startedAt.localeCompare(b.startedAt));

  const lines: InvoiceLine[] = trips.map((trip) => {
    const passenger = params.passengers.get(trip.passengerId);
    const from = params.addresses.get(trip.fromAddressId)?.label ?? '?';
    const to = params.addresses.get(trip.toAddressId)?.label ?? '?';
    const netCents = netFromGross(trip.fareGrossCents, trip.vatRatePercent);

    return {
      id: crypto.randomUUID(),
      tripId: trip.id,
      description:
        `Personenbeförderung am ${formatDate(trip.date)}` +
        (passenger ? `, ${passengerDisplayName(passenger)}` : '') +
        `\n${from} → ${to} (${formatKm(trip.distanceMeters)} km)`,
      quantity: 1,
      unit: 'Fahrt',
      unitPriceNetCents: netCents,
      vatRatePercent: trip.vatRatePercent,
    };
  });

  const customerPassenger = params.customerPassengerId
    ? params.passengers.get(params.customerPassengerId)
    : undefined;

  const invoice: Invoice = {
    id: crypto.randomUUID(),
    // A draft carries no number. It gets one at issue.
    invoiceNumber: '',
    numberCircle: '',
    status: 'entwurf',
    issueDate,
    serviceDateFrom: trips[0]?.date ?? issueDate,
    serviceDateTo: trips[trips.length - 1]?.date ?? issueDate,
    dueDate: addDays(issueDate, params.paymentTermDays ?? 14),
    customerPassengerId: params.customerPassengerId,
    customerName:
      params.customerName ??
      (customerPassenger
        ? customerPassenger.company || passengerDisplayName(customerPassenger)
        : ''),
    customerAddress: params.customerAddress ?? '',
    customerVatId: params.customerVatId ?? customerPassenger?.vatId,
    lines,
    netTotalCents: 0,
    vatTotalCents: 0,
    grossTotalCents: 0,
    isKleinbetrag: false,
    isKleinunternehmer: params.company.kleinunternehmer,
    paymentTerms: params.company.invoicePaymentTerms,
    dunningLevel: 0,
    eInvoiceFormat: 'keine',
    createdAt: nowIso(),
  };

  return recalculate(invoice, params.company);
}

/**
 * Recompute totals. VAT is calculated per rate bucket on the summed net, not
 * per line, so the breakdown adds up exactly — EN 16931 rejects an invoice
 * whose stated totals disagree with its lines by a cent.
 */
export function recalculate(invoice: Invoice, company: CompanySettings): Invoice {
  const lineNets = invoice.lines.map((line) => ({
    netCents: lineNetCents(line.quantity, line.unitPriceNetCents),
    vatRatePercent: company.kleinunternehmer ? 0 : line.vatRatePercent,
  }));

  const buckets = summariseVat(lineNets);
  const netTotalCents = buckets.reduce((sum, bucket) => sum + bucket.netCents, 0);
  const vatTotalCents = buckets.reduce((sum, bucket) => sum + bucket.vatCents, 0);
  const grossTotalCents = netTotalCents + vatTotalCents;

  return {
    ...invoice,
    lines: company.kleinunternehmer
      ? invoice.lines.map((line) => ({ ...line, vatRatePercent: 0 }))
      : invoice.lines,
    netTotalCents,
    vatTotalCents,
    grossTotalCents,
    isKleinunternehmer: company.kleinunternehmer,
    isKleinbetrag: grossTotalCents <= KLEINBETRAG_LIMIT_CENTS,
  };
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

export interface InvoiceProblem {
  field: string;
  /** Rendered late, so the wording follows the interface language. */
  message: (t?: Dictionary) => string;
  /** German citation in both languages: § 14 Abs. 4 UStG has no translation. */
  legalBasis?: string;
}

/**
 * Check the § 14 Abs. 4 UStG mandatory fields.
 *
 * A Kleinbetragsrechnung under § 33 UStDV needs far fewer of them, so the
 * check is relaxed accordingly rather than nagging about a recipient address
 * the law does not require on a 40-euro ride.
 */
export function validateInvoice(invoice: Invoice, company: CompanySettings): InvoiceProblem[] {
  const problems: InvoiceProblem[] = [];

  if (!company.companyName.trim()) {
    problems.push({
      field: 'company',
      message: (t = de) => t.invoices.problems.companyName,
      legalBasis: '§ 14 Abs. 4 Nr. 1 UStG',
    });
  }
  if (!company.street.trim() || !company.city.trim()) {
    problems.push({
      field: 'company',
      message: (t = de) => t.invoices.problems.companyAddress,
      legalBasis: '§ 14 Abs. 4 Nr. 1 UStG',
    });
  }
  if (!company.taxNumber?.trim() && !company.vatId?.trim()) {
    problems.push({
      field: 'taxNumber',
      message: (t = de) => t.invoices.problems.taxNumber,
      legalBasis: '§ 14 Abs. 4 Nr. 2 UStG',
    });
  }

  if (invoice.lines.length === 0) {
    problems.push({
      field: 'lines',
      message: (t = de) => t.invoices.problems.noLines,
      legalBasis: '§ 14 Abs. 4 Nr. 5 UStG',
    });
  }
  for (const line of invoice.lines) {
    if (!line.description.trim()) {
      problems.push({
        field: 'lines',
        message: (t = de) => t.invoices.problems.lineDescription,
        legalBasis: '§ 14 Abs. 4 Nr. 5 UStG',
      });
      break;
    }
  }

  if (!invoice.serviceDateFrom) {
    problems.push({
      field: 'serviceDate',
      message: (t = de) => t.invoices.problems.serviceDate,
      legalBasis: '§ 14 Abs. 4 Nr. 6 UStG',
    });
  }

  // Below the § 33 UStDV threshold the recipient's name and address are not
  // required, so only ask for them above it.
  if (!invoice.isKleinbetrag) {
    if (!invoice.customerName.trim()) {
      problems.push({
        field: 'customerName',
        message: (t = de) => t.invoices.problems.customerName,
        legalBasis: '§ 14 Abs. 4 Nr. 1 UStG',
      });
    }
    if (!invoice.customerAddress.trim()) {
      problems.push({
        field: 'customerAddress',
        message: (t = de) => t.invoices.problems.customerAddress,
        legalBasis: '§ 14 Abs. 4 Nr. 1 UStG',
      });
    }
  }

  return problems;
}

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------

export async function saveDraft(ctx: AuditContext, invoice: Invoice): Promise<Invoice> {
  const existing = await invoiceRepo.get(ctx.dataKey, invoice.id);
  if (existing) {
    if (existing.status !== 'entwurf') {
      throw new InvoiceIssuedError();
    }
    return invoiceRepo.update(ctx, invoice.id, invoice);
  }
  return invoiceRepo.create(ctx, invoice);
}

export class InvoiceIssuedError extends Error {
  constructor() {
    super(
      'Eine gestellte Rechnung wird nicht geändert. Stornieren Sie sie und stellen Sie eine neue aus.',
    );
    this.name = 'InvoiceIssuedError';
  }
}

export class InvoiceIncompleteError extends Error {
  constructor(readonly problems: InvoiceProblem[]) {
    super(`Die Rechnung ist unvollständig: ${problems.map((p) => p.message()).join(' ')}`);
    this.name = 'InvoiceIncompleteError';
  }
}

/**
 * Issue the invoice: allocate the number, freeze the content, and mark the
 * trips as billed.
 */
export async function issueInvoice(
  ctx: AuditContext,
  invoice: Invoice,
  company: CompanySettings,
  config: NumberCircleConfig = DEFAULT_NUMBER_CIRCLE,
): Promise<Invoice> {
  const problems = validateInvoice(invoice, company);
  if (problems.length > 0) throw new InvoiceIncompleteError(problems);

  const circle = numberCircleFor(config, invoice.issueDate);
  const existingNumbers = await allInvoiceNumbers(circle);
  const { invoiceNumber } = nextInvoiceNumber(config, invoice.issueDate, existingNumbers);

  const issued: Invoice = {
    ...recalculate(invoice, company),
    invoiceNumber,
    numberCircle: circle,
    status: 'offen',
    issuedAt: nowIso(),
  };

  const saved = (await invoiceRepo.get(ctx.dataKey, invoice.id))
    ? await invoiceRepo.update(ctx, invoice.id, issued, {
        action: 'update',
        reason: `Rechnung ${invoiceNumber} gestellt`,
      })
    : await invoiceRepo.create(ctx, issued);

  await relinkTrips(ctx, issued.lines, issued.id);

  return saved;
}

/**
 * Point the billed trips at the invoice, or release them again when it is
 * cancelled.
 *
 * A trip that is no longer in the working set is skipped rather than treated
 * as an error. The only route to a dangling reference is a restore from a
 * backup older than the invoice, and refusing to cancel an invoice because one
 * of its trips has gone missing would leave the operator holding a document
 * they cannot correct.
 */
async function relinkTrips(
  ctx: AuditContext,
  lines: readonly InvoiceLine[],
  invoiceId: Uuid | undefined,
): Promise<void> {
  for (const line of lines) {
    if (!line.tripId) continue;
    if (!(await tripRepo.get(ctx.dataKey, line.tripId))) continue;
    await tripRepo.update(ctx, line.tripId, { invoiceId });
  }
}

export async function markPaid(
  ctx: AuditContext,
  invoiceId: Uuid,
  paidAmountCents: number,
  paidAt = nowIso(),
): Promise<Invoice> {
  return invoiceRepo.update(
    ctx,
    invoiceId,
    { status: 'bezahlt', paidAt, paidAmountCents },
    { reason: 'Zahlungseingang erfasst' },
  );
}

export async function markUnpaid(ctx: AuditContext, invoiceId: Uuid): Promise<Invoice> {
  return invoiceRepo.update(
    ctx,
    invoiceId,
    { status: 'offen', paidAt: undefined, paidAmountCents: undefined },
    { reason: 'Zahlungseingang zurückgenommen' },
  );
}

export async function raiseDunningLevel(ctx: AuditContext, invoice: Invoice): Promise<Invoice> {
  const next = Math.min(invoice.dunningLevel + 1, 3) as DunningLevel;
  return invoiceRepo.update(
    ctx,
    invoice.id,
    { dunningLevel: next, lastDunnedAt: nowIso(), status: 'ueberfaellig' },
    { reason: `${dunningLabel(next)} erstellt` },
  );
}

/**
 * German by default — the dunning PDF and the audit reason must stay German.
 * Screens pass their dictionary to translate the same wording on the surface.
 */
export function dunningLabel(level: DunningLevel, t: Dictionary = de): string {
  return t.labels.dunning[level];
}

/**
 * Cancel an invoice with a Stornorechnung.
 *
 * The cancellation is a new document with its own number and inverted amounts,
 * not a deletion. The original keeps its number so the sequence stays gapless.
 */
export async function cancelInvoice(
  ctx: AuditContext,
  invoice: Invoice,
  company: CompanySettings,
  reason: string,
  config: NumberCircleConfig = DEFAULT_NUMBER_CIRCLE,
): Promise<{ cancelled: Invoice; storno: Invoice }> {
  const issueDate = todayIso();
  const circle = numberCircleFor(config, issueDate);
  const existingNumbers = await allInvoiceNumbers(circle);
  const { invoiceNumber } = nextInvoiceNumber(config, issueDate, existingNumbers);

  const storno: Invoice = {
    ...invoice,
    id: crypto.randomUUID(),
    invoiceNumber,
    numberCircle: circle,
    status: 'offen',
    issueDate,
    dueDate: issueDate,
    lines: invoice.lines.map((line) => ({
      ...line,
      id: crypto.randomUUID(),
      description: `Storno: ${line.description}`,
      unitPriceNetCents: -line.unitPriceNetCents,
    })),
    netTotalCents: -invoice.netTotalCents,
    vatTotalCents: -invoice.vatTotalCents,
    grossTotalCents: -invoice.grossTotalCents,
    notes: `Stornorechnung zu ${invoice.invoiceNumber}. Grund: ${reason}`,
    cancelsInvoiceId: invoice.id,
    paidAt: undefined,
    paidAmountCents: undefined,
    dunningLevel: 0,
    lastDunnedAt: undefined,
    eInvoiceGeneratedAt: undefined,
    createdAt: nowIso(),
    issuedAt: nowIso(),
  };

  await invoiceRepo.create(ctx, recalculate(storno, company));

  const cancelled = await invoiceRepo.update(
    ctx,
    invoice.id,
    { status: 'storniert' },
    { reason: `Storniert durch ${invoiceNumber}: ${reason}` },
  );

  // The trips become billable again, otherwise a cancellation would silently
  // lose revenue.
  await relinkTrips(ctx, invoice.lines, undefined);

  return { cancelled, storno };
}

/** Invoices past their due date that are still open. */
export function isOverdue(invoice: Invoice, today = todayIso()): boolean {
  return (invoice.status === 'offen' || invoice.status === 'ueberfaellig') && invoice.dueDate < today;
}

export function invoiceFooterNote(invoice: Invoice, company: CompanySettings): string | null {
  if (invoice.isKleinunternehmer) {
    return exemptionNote(0, true);
  }
  return company.invoiceFooterNote?.trim() || null;
}

/** German status wording, for the exports. Screens read the active dictionary. */
export const STATUS_LABELS: Record<Invoice['status'], string> = de.labels.invoiceStatus;
